import React, { type Ref } from 'react';

import { useIntl } from 'react-intl-next';

import CustomizeIcon from '@atlaskit/icon/core/customize';
import { ButtonMenuItem } from '@atlaskit/navigation-system';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';
import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts';

import type { NavItemConfigInternal } from '../../../common/types';
import { extendWithCustomizationOptions } from '../../../common/utils/extend-with-customization-options';
import { useOnSave } from '../../../common/utils/use-on-save';
import { useUserMenuDataShapes } from '../../../controllers/user-state/user-menu-data-shapes-provider';
import { customizationModalEntryPoint } from '../../customization-modal/entrypoint';

import { messages } from './messages';
import { useGetMenuIconAndLabel } from './use-get-menu-icon-and-label';

type CustomizeSidebarMenuItemProps = {
	appLabel: string;
	items: Record<string, NavItemConfigInternal>;
	onClick?: () => void;
	shortcuts: Record<string, GlobalShortcutsItem>;
	testId?: string;
};

export const CustomizeSidebarMenuItem = ({
	appLabel,
	items,
	shortcuts,
	testId,
	onClick,
}: CustomizeSidebarMenuItemProps) => {
	const getMenuIconAndLabel = useGetMenuIconAndLabel(items, shortcuts);
	const { itemUserMenuData, shortcutUserMenuData } = useUserMenuDataShapes();
	const itemExtendedUserMenuData = extendWithCustomizationOptions(itemUserMenuData, items);

	const onSave = useOnSave();
	const { formatMessage } = useIntl();

	return (
		<ModalTrigger
			entryPoint={customizationModalEntryPoint}
			entryPointProps={{
				getMenuIconAndLabel,
				itemExtendedUserMenuData,
				shortcutExtendedUserMenuData: shortcutUserMenuData,
				onSave,
				appLabel,
			}}
		>
			{({ ref }) => (
				<ButtonMenuItem
					elemBefore={<CustomizeIcon label="" />}
					ref={ref as Ref<HTMLButtonElement>}
					testId={testId}
					onClick={onClick}
				>
					{formatMessage(messages.customizeSidebarMenuItemLabel)}
				</ButtonMenuItem>
			)}
		</ModalTrigger>
	);
};
